// material-ui
import { useTheme } from '@mui/material/styles';
import { Grid, Skeleton } from '@mui/material';

// project imports
import IntegrationEventsAreaChartCard from './IntegrationEventsAreaChartCard';
import LatestCustomerTableCard from './LatestCustomerTableCard';
import IntegrationSummaryCard from './IntegrationSummaryCard';
import { gridSpacing } from 'store/constant';

// assets
import { IconPackgeImport } from '@tabler/icons';
import MonetizationOnTwoToneIcon from '@mui/icons-material/MonetizationOnTwoTone';
import axios from 'utils/axios';
import React, { useState } from 'react';

// ==============================|| ANALYTICS DASHBOARD ||============================== //

const Analytics = () => {
    const theme = useTheme();
    const [series, setSeries] = useState<{ name: string; data: any }[] | undefined>();
    const [totals, setTotals] = useState<any>();

    const fetchData = async () => {
        const [inventoryStats, pricesStats] = await Promise.all([
            axios.get(process.env.REACT_APP_INVENTORY_INSIGHTS_URL || ''),
            axios.get(process.env.REACT_APP_PRICES_INSIGHTS_URL || '')
        ]);

        const [inventoryEvents, priceEvents] = [inventoryStats.data, pricesStats.data].map((i: any) =>
            i.map((j: any) => ({
                ...j,
                time: j.time,
                success: Math.round(j.success),
                errors: Math.round(j.errors)
            }))
        );

        const [cInventoryEvents, cPriceEvents] = [inventoryEvents, priceEvents].map((i: any) =>
            i.reduce((previousValue: number, currentValue: any) => previousValue + currentValue.count, 0)
        );

        const [inventorySuccess, pricesSuccess] = [inventoryEvents, priceEvents].map((i: any) =>
            i.reduce((previousValue: number, currentValue: any) => previousValue + currentValue.success, 0)
        );

        const [inventoryError, pricesError] = [inventoryEvents, priceEvents].map((i: any) =>
            i.reduce((previousValue: number, currentValue: any) => previousValue + currentValue.errors, 0)
        );

        const sEvents = inventorySuccess + pricesSuccess + inventoryError + pricesError;

        setSeries([
            { name: 'Inventory Success', data: inventoryEvents.map((i: any) => ({ y: i.success, x: i.time })) },
            { name: 'Inventory Errors', data: inventoryEvents.map((i: any) => ({ y: i.errors, x: i.time })) },
            { name: 'Prices Success', data: priceEvents.map((i: any) => ({ y: i.success, x: i.time })) },
            { name: 'Prices Errors', data: priceEvents.map((i: any) => ({ y: i.errors, x: i.time })) }
        ]);

        const range =
            inventoryEvents.length > 0
                ? `from ${new Date(inventoryEvents[0].time).toLocaleString()} to ${new Date(
                      inventoryEvents[inventoryEvents.length - 1].time
                  ).toLocaleString()}`
                : '';

        const inventoryTotal = inventorySuccess + inventoryError;
        const inventoryRate = inventoryTotal > 0 ? (inventorySuccess / inventoryTotal) * 100 : 100;

        const pricesTotal = pricesSuccess + pricesError;
        const pricesRate = pricesTotal > 0 ? (pricesSuccess / pricesTotal) * 100 : 100;

        setTotals({
            range,
            pSuccess: `${Math.round(((inventorySuccess + pricesSuccess) / sEvents) * 100)}%`,
            pError: `${Math.round(((inventoryError + pricesError) / sEvents) * 100)}%`,
            sSuccess: `${inventorySuccess + pricesSuccess}`,
            sInventorySuccess: inventorySuccess,
            sPricesSuccess: pricesSuccess,
            sError: `${inventoryError + pricesError}`,
            events: cInventoryEvents + cPriceEvents,
            inventoryError,
            inventoryTotal,
            inventoryRate,
            pricesError,
            pricesTotal,
            pricesRate
        });
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    return (
        <Grid container spacing={gridSpacing}>
            <Grid item xs={12} lg={12} md={12}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <IntegrationEventsAreaChartCard series={series} totals={totals} />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        {totals ? (
                            <IntegrationSummaryCard
                                title="Inventory Integration"
                                successCount={totals.sInventorySuccess}
                                errorCount={totals.inventoryError}
                                totalCount={totals.inventoryTotal}
                                successRate={totals.inventoryRate}
                                icon={IconPackgeImport}
                                color={theme.palette.secondary.main}
                            />
                        ) : (
                            <Skeleton variant="rectangular" height={180} />
                        )}
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        {totals ? (
                            <IntegrationSummaryCard
                                title="Prices Integration"
                                successCount={totals.sPricesSuccess}
                                errorCount={totals.pricesError}
                                totalCount={totals.pricesTotal}
                                successRate={totals.pricesRate}
                                icon={MonetizationOnTwoToneIcon}
                                color={theme.palette.primary.main}
                            />
                        ) : (
                            <Skeleton variant="rectangular" height={180} />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <LatestCustomerTableCard title="Store Statistics" />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Analytics;
